import * as React from "react";
import styled from "styled-components";
import { flexRender, type Header } from "@tanstack/react-table";
import { TableHeaderCell } from "@sproutsocial/seeds-react-table/v2";
import { Icon } from "@sproutsocial/seeds-react-icon";

const StyledTableHeaderCell = styled(TableHeaderCell)<{ $canSort: boolean }>`
  cursor: ${({ $canSort }) => ($canSort ? "pointer" : "default")};
  user-select: none;
`;

export interface TypeDataTableHeaderCellProps<
  TData extends Record<string, unknown>
> extends Omit<React.ComponentProps<typeof TableHeaderCell>, "children"> {
  /**
   * The header data from TanStack Table
   */
  header: Header<TData, unknown>;
}

/**
 * DataTableHeaderCell component for rendering table header cells
 *
 * Renders a header cell with sorting functionality.
 * Displays sort icons (caret-up-solid for ascending, caret-down-solid for descending,
 * caret-up-down-outline for unsorted) and handles sort toggle clicks.
 * Combines internal sort handler with optional custom onClick handler.
 * All props are passed through to the underlying TableHeaderCell component.
 *
 * **Performance Note:** The onClick handler is not memoized. If you pass a custom
 * onClick prop that's expensive to recreate, memoize it with useCallback in your component.
 *
 * @example
 * ```tsx
 * <DataTableHeaderCell header={header} />
 * ```
 *
 * @example
 * With custom onClick handler (memoize if needed):
 * ```tsx
 * const handleClick = useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
 *   console.log('Header clicked');
 * }, []);
 *
 * <DataTableHeaderCell header={header} onClick={handleClick} />
 * ```
 */
export const DataTableHeaderCell = <TData extends Record<string, unknown>>({
  header,
  onClick,
  ...tableHeaderCellProps
}: TypeDataTableHeaderCellProps<TData>) => {
  const canSort = header.column.getCanSort();
  const sortHandler = header.column.getToggleSortingHandler();

  return (
    <StyledTableHeaderCell
      $canSort={canSort}
      id={header.id}
      onClick={(e: React.MouseEvent<HTMLTableCellElement>) => {
        // Execute sort handler first, then custom onClick if provided
        // Consumer should memoize onClick if needed for performance
        if (sortHandler) {
          sortHandler(e);
        }
        onClick?.(e);
      }}
      {...tableHeaderCellProps}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      {canSort && (
        <Icon
          size="mini"
          name={
            header.column.getIsSorted() === "asc"
              ? "caret-up-down-sort-up-outline"
              : header.column.getIsSorted() === "desc"
              ? "caret-up-down-sort-down-outline"
              : "caret-up-down-outline"
          }
          style={{ padding: "0 4px" }}
        />
      )}
    </StyledTableHeaderCell>
  );
};
